/**
 * Offer Import Page - Legacy-Angebot Import mit Review
 * 
 * Features:
 * - PDF Upload
 * - Split-View: PDF links, Daten rechts
 * - Editierbare Positionen
 * - Validierung
 * - Import-Abschluss
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  Edit,
  Eye,
  RefreshCw,
  AlertTriangle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Formatierung
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
};

const formatPercent = (value) => {
  return `${value.toFixed(1)}%`;
};

// Item Types
// Item Types (Kategorien)
const ITEM_TYPES = [
  { value: "material", label: "Material", color: "bg-blue-100 text-blue-700" },
  { value: "montage", label: "Montage", color: "bg-green-100 text-green-700" },
  { value: "elektro", label: "Elektro", color: "bg-yellow-100 text-yellow-700" },
  { value: "planung", label: "Planung", color: "bg-purple-100 text-purple-700" },
  { value: "sonstiges", label: "Sonstiges", color: "bg-gray-100 text-gray-700" },
  { value: "labor", label: "Arbeitsleistung", color: "bg-orange-100 text-orange-700" },
  { value: "service", label: "Dienstleistung", color: "bg-teal-100 text-teal-700" },
  { value: "flat_rate", label: "Pauschale", color: "bg-pink-100 text-pink-700" },
  { value: "other", label: "Sonstiges", color: "bg-gray-100 text-gray-700" }
];

export default function OfferImportPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [step, setStep] = useState("upload"); // upload, review, complete
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  
  // Form Data
  const [importFormat, setImportFormat] = useState("standard");
  const [customerId, setCustomerId] = useState(searchParams.get("customer_id") || "");
  const [customers, setCustomers] = useState([]);
  const [headerData, setHeaderData] = useState({
    offer_number: "",
    project_number: "",
    offer_date: "",
    vat_rate: 19,
    title: ""
  });
  const [positions, setPositions] = useState([]);
  const [discount, setDiscount] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);
  
  // Validation
  const [warnings, setWarnings] = useState([]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/customers/`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.data || data);
      }
    } catch (error) {
      console.error("Failed to load customers:", error);
    }
  }, [token]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const positionsTotal = positions
      .filter(p => p.is_billable !== false)
      .reduce((sum, p) => sum + (p.total_price_net || 0), 0);
    
    let discountAmount = 0;
    if (discount) {
      if (discount.type === "percentage") {
        discountAmount = positionsTotal * discount.value / 100;
      } else {
        discountAmount = discount.value;
      }
    }
    
    const subtotalAfterDiscount = positionsTotal - discountAmount;
    const vatAmount = subtotalAfterDiscount * headerData.vat_rate / 100;
    const totalGross = subtotalAfterDiscount + vatAmount;
    
    return {
      positionsTotal,
      discountAmount,
      subtotalAfterDiscount,
      vatAmount,
      totalGross
    };
  }, [positions, discount, headerData.vat_rate]);

  // Validate
  const validateData = useCallback(() => {
    const newWarnings = [];
    const totals = calculateTotals();
    
    // Prüfe auf fehlende Preise
    const missingPrices = positions.filter(p => !p.total_price_net && p.is_billable !== false);
    if (missingPrices.length > 0) {
      newWarnings.push(`${missingPrices.length} Position(en) ohne Preis`);
    }
    
    // Prüfe Summenabgleich wenn Brutto aus PDF vorhanden
    if (parsedData?.total_gross) {
      const diff = Math.abs(totals.totalGross - parsedData.total_gross);
      if (diff > 1) {
        newWarnings.push(
          `Summenabweichung: Berechnet ${formatCurrency(totals.totalGross)}, ` +
          `PDF ${formatCurrency(parsedData.total_gross)}`
        );
      }
    }
    
    // Prüfe auf Kunde
    if (!customerId) {
      newWarnings.push("Kein Kunde ausgewählt");
    }
    
    setWarnings(newWarnings);
    return newWarnings;
  }, [positions, parsedData, customerId, calculateTotals]);

  useEffect(() => {
    if (step === "review") {
      validateData();
    }
  }, [step, positions, discount, customerId, validateData]);

  // Handle PDF Upload
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Bitte eine PDF-Datei auswählen");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (customerId) {
        formData.append("customer_id", customerId);
      }
      formData.append("use_ai", "true");
      formData.append("import_format", importFormat);
      
      const res = await fetch(`${API_URL}/api/offer-import/upload`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Upload fehlgeschlagen");
      }
      
      const data = await res.json();
      
      // Kombi-Angebot: direkt auf die spezialisierte Review-Maske weiterleiten
      if (importFormat === "sunprojects_combined") {
        toast.success("Kombi-Angebot erkannt — wechsle zur Aufteilungs-Maske");
        navigate(`/angebote-import/kombi/${data.id}`);
        return;
      }
      
      setImportFile(data);
      
      // Parse result
      const parsed = data.parsed_data_json;
      if (parsed) {
        setParsedData(parsed);
        
        // Header setzen
        setHeaderData({
          offer_number: parsed.offer_number || "",
          project_number: parsed.project_number || "",
          offer_date: parsed.offer_date || "",
          vat_rate: parsed.vat_rate ?? 19,
          title: `Import ${parsed.offer_number || file.name.replace(".pdf", "")}`
        });
        
        // Positionen setzen mit Kategorie-Mapping
        if (parsed.positions) {
          setPositions(parsed.positions.map((p, i) => ({
            ...p,
            id: `pos-${i}`,
            is_billable: true,
            is_optional: false,
            // Verwende die AI-erkannte Kategorie oder fallback
            item_type: p.category || "sonstiges"
          })));
        }
        
        // Rabatt setzen
        if (parsed.discount) {
          setDiscount(parsed.discount);
        }
        
        // PDF URL
        if (data.storage_key) {
          // Hier müsste die tatsächliche PDF-URL konstruiert werden
          setPdfUrl(`${API_URL}/api/storage/download/${encodeURIComponent(data.storage_key)}`);
        }
        
        setStep("review");
        toast.success("PDF erfolgreich analysiert");
      } else {
        toast.error("Konnte keine Daten aus PDF extrahieren");
      }
      
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  // Update position
  const updatePosition = (index, field, value) => {
    setPositions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Neuberechnung total_price_net
      if (field === "quantity" || field === "unit_price_net") {
        const qty = field === "quantity" ? value : updated[index].quantity || 1;
        const price = field === "unit_price_net" ? value : updated[index].unit_price_net || 0;
        updated[index].total_price_net = qty * price;
      }
      
      return updated;
    });
  };

  // Delete position
  const deletePosition = (index) => {
    setPositions(prev => prev.filter((_, i) => i !== index));
    toast.success("Position gelöscht");
  };

  // Add position
  const addPosition = () => {
    const nextNo = positions.length + 1;
    setPositions(prev => [...prev, {
      id: `pos-new-${Date.now()}`,
      line_no: `${nextNo}.1`,
      title: "Neue Position",
      description: "",
      quantity: 1,
      unit: "Stk",
      unit_price_net: 0,
      total_price_net: 0,
      is_billable: true,
      is_optional: false,
      item_type: "other",
      confidence: 1.0
    }]);
  };

  // Finalize import
  const handleFinalize = async () => {
    const validationWarnings = validateData();
    
    // Harte Fehler
    if (!customerId) {
      toast.error("Bitte wählen Sie einen Kunden aus");
      return;
    }
    
    if (positions.length === 0) {
      toast.error("Keine Positionen vorhanden");
      return;
    }
    
    // Warnungen bestätigen
    if (validationWarnings.length > 0) {
      const confirmed = window.confirm(
        `Es gibt Warnungen:\n\n${validationWarnings.join("\n")}\n\nTrotzdem fortfahren?`
      );
      if (!confirmed) return;
    }
    
    setSaving(true);
    
    try {
      const res = await fetch(`${API_URL}/api/offer-import/finalize/${importFile.id}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          customer_id: customerId,
          positions: positions.map(p => ({
            line_no: p.line_no,
            title: p.title,
            description: p.description,
            quantity: p.quantity,
            unit: p.unit,
            unit_price_net: p.unit_price_net,
            total_price_net: p.total_price_net,
            item_type: p.item_type,
            is_optional: p.is_optional,
            is_billable: p.is_billable,
            source_text_raw: p.source_text_raw,
            confidence: p.confidence
          })),
          discount: discount,
          header_data: headerData
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Import fehlgeschlagen");
      }
      
      const offer = await res.json();
      
      setStep("complete");
      toast.success("Angebot erfolgreich importiert!");
      
      // Navigiere zum Angebot
      setTimeout(() => {
        navigate(`/angebote-import/${offer.id}`);
      }, 1500);
      
    } catch (error) {
      console.error("Finalize failed:", error);
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Legacy-Angebot importieren</h1>
            <p className="text-muted-foreground">
              PDF hochladen, prüfen und als CRM-Angebot speichern
            </p>
          </div>
        </div>
        
        {/* Step Indicator */}
        <div className="flex items-center gap-2">
          <Badge variant={step === "upload" ? "default" : "secondary"}>
            1. Upload
          </Badge>
          <span className="text-muted-foreground">→</span>
          <Badge variant={step === "review" ? "default" : "secondary"}>
            2. Review
          </Badge>
          <span className="text-muted-foreground">→</span>
          <Badge variant={step === "complete" ? "default" : "secondary"}>
            3. Fertig
          </Badge>
        </div>
      </div>

      {/* STEP 1: Upload */}
      {step === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              PDF-Angebot hochladen
            </CardTitle>
            <CardDescription>
              Laden Sie ein PDF-Angebot hoch. Das System extrahiert automatisch Positionen, Preise und Summen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Format-Auswahl */}
            <div className="space-y-2">
              <Label>Import-Format</Label>
              <Select value={importFormat} onValueChange={setImportFormat}>
                <SelectTrigger data-testid="import-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (1 Angebot – Auto-Erkennung)</SelectItem>
                  <SelectItem value="sunprojects_combined">Sunprojects Kombi-Angebot (PV + Wärmepumpe → 2 Angebote)</SelectItem>
                </SelectContent>
              </Select>
              {importFormat === "sunprojects_combined" && (
                <p className="text-xs text-muted-foreground">
                  Das PDF wird automatisch in ein PV-Angebot (0% MwSt.) und ein Wärmepumpen-Angebot (19% MwSt., 5 Produktgruppen) aufgesplittet.
                </p>
              )}
            </div>

            {/* Kunde auswählen */}
            <div className="space-y-2">
              <Label>Kunde (optional)</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kunde auswählen oder später zuordnen" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Upload Zone */}
            <div className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleUpload}
                className="hidden"
                id="pdf-upload"
                disabled={uploading}
              />
              <label
                htmlFor="pdf-upload"
                className="cursor-pointer flex flex-col items-center gap-4"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-12 w-12 text-primary animate-spin" />
                    <p>PDF wird analysiert...</p>
                  </>
                ) : (
                  <>
                    <FileText className="h-12 w-12 text-muted-foreground" />
                    <div>
                      <p className="font-medium">PDF hier ablegen oder klicken</p>
                      <p className="text-sm text-muted-foreground">
                        Unterstützt: Angebote, Auftragsbestätigungen, Kostenvoranschläge
                      </p>
                    </div>
                  </>
                )}
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Review */}
      {step === "review" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: PDF Preview (optional) */}
          <Card className="lg:row-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Original-PDF
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-[600px] border rounded"
                  title="PDF Preview"
                />
              ) : (
                <div className="h-[600px] bg-muted/50 rounded flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2" />
                    <p>PDF-Vorschau nicht verfügbar</p>
                    <p className="text-sm">
                      Datei: {importFile?.original_filename}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Parsed Data */}
          <div className="space-y-6">
            {/* Warnings */}
            {warnings.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-disc list-inside">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Parse Confidence */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {parsedData?.overall_confidence > 0.7 ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                    )}
                    <span>
                      Erkennungsqualität: {formatPercent((parsedData?.overall_confidence || 0) * 100)}
                    </span>
                  </div>
                  <Badge variant="outline">
                    Methode: {parsedData?.parse_method === "ai" ? "KI" : "Regex"}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Header Data */}
            <Card>
              <CardHeader>
                <CardTitle>Angebotsdaten</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Angebotsnummer</Label>
                    <Input
                      value={headerData.offer_number}
                      onChange={(e) => setHeaderData(prev => ({ ...prev, offer_number: e.target.value }))}
                      data-testid="input-offer-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Projektnummer</Label>
                    <Input
                      value={headerData.project_number}
                      onChange={(e) => setHeaderData(prev => ({ ...prev, project_number: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={headerData.offer_date}
                      onChange={(e) => setHeaderData(prev => ({ ...prev, offer_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>MwSt-Satz (%)</Label>
                    <Input
                      type="number"
                      value={headerData.vat_rate}
                      onChange={(e) => {
                        const v = e.target.value;
                        const parsed = parseFloat(v);
                        setHeaderData(prev => ({ ...prev, vat_rate: v === "" || Number.isNaN(parsed) ? 19 : parsed }));
                      }}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Kunde</Label>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger className={!customerId ? "border-red-500" : ""}>
                      <SelectValue placeholder="Kunde auswählen (Pflicht)" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Discount */}
            {discount && (
              <Card>
                <CardHeader>
                  <CardTitle>Rabatt</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <Select
                      value={discount.type}
                      onValueChange={(v) => setDiscount(prev => ({ ...prev, type: v }))}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Prozentual</SelectItem>
                        <SelectItem value="absolute">Absolut</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={discount.value}
                      onChange={(e) => setDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                      className="w-32"
                    />
                    <span>{discount.type === "percentage" ? "%" : "€"}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDiscount(null)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Positions Table - Full Width */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Positionen ({positions.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={addPosition}>
                  <Plus className="h-4 w-4 mr-2" />
                  Position hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-auto max-h-[500px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead className="w-16">Pos.</TableHead>
                      <TableHead>Titel</TableHead>
                      <TableHead className="w-20">Menge</TableHead>
                      <TableHead className="w-20">Einheit</TableHead>
                      <TableHead className="w-28 text-right">EP (netto)</TableHead>
                      <TableHead className="w-28 text-right">Gesamt</TableHead>
                      <TableHead className="w-24">Typ</TableHead>
                      <TableHead className="w-20 text-center">Opt.</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {positions.map((pos, idx) => (
                      <TableRow key={pos.id} className={pos.is_optional ? "opacity-60" : ""}>
                        <TableCell>
                          <Input
                            value={pos.line_no}
                            onChange={(e) => updatePosition(idx, "line_no", e.target.value)}
                            className="w-16 h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={pos.title}
                            onChange={(e) => updatePosition(idx, "title", e.target.value)}
                            className="h-8 text-sm"
                          />
                          {pos.description && (
                            <p className="text-xs text-muted-foreground mt-1 truncate max-w-md">
                              {pos.description}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={pos.quantity}
                            onChange={(e) => updatePosition(idx, "quantity", parseFloat(e.target.value) || 0)}
                            className="w-20 h-8 text-sm text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={pos.unit}
                            onChange={(e) => updatePosition(idx, "unit", e.target.value)}
                            className="w-20 h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            value={pos.unit_price_net || ""}
                            onChange={(e) => updatePosition(idx, "unit_price_net", parseFloat(e.target.value) || 0)}
                            className="w-28 h-8 text-sm text-right"
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(pos.total_price_net)}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={pos.item_type}
                            onValueChange={(v) => updatePosition(idx, "item_type", v)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ITEM_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={pos.is_optional}
                            onCheckedChange={(checked) => updatePosition(idx, "is_optional", checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePosition(idx)}
                            className="h-8 w-8"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Summary & Actions */}
          <Card className="lg:col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                {/* Totals */}
                <div className="space-y-2">
                  <div className="flex justify-between gap-8">
                    <span>Summe Positionen (netto):</span>
                    <span className="font-medium">{formatCurrency(totals.positionsTotal)}</span>
                  </div>
                  {discount && (
                    <div className="flex justify-between gap-8 text-red-600">
                      <span>
                        Rabatt ({discount.type === "percentage" ? `${discount.value}%` : "absolut"}):
                      </span>
                      <span>- {formatCurrency(totals.discountAmount)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between gap-8">
                    <span>Netto nach Rabatt:</span>
                    <span className="font-medium">{formatCurrency(totals.subtotalAfterDiscount)}</span>
                  </div>
                  <div className="flex justify-between gap-8">
                    <span>MwSt ({headerData.vat_rate}%):</span>
                    <span>{formatCurrency(totals.vatAmount)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between gap-8 text-lg font-bold">
                    <span>Gesamt (brutto):</span>
                    <span>{formatCurrency(totals.totalGross)}</span>
                  </div>
                  
                  {parsedData?.total_gross && (
                    <div className="flex justify-between gap-8 text-sm text-muted-foreground">
                      <span>PDF-Betrag:</span>
                      <span>{formatCurrency(parsedData.total_gross)}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep("upload")}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Zurück
                  </Button>
                  <Button onClick={handleFinalize} disabled={saving}>
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Angebot importieren
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* STEP 3: Complete */}
      {step === "complete" && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Import erfolgreich!</h2>
            <p className="text-muted-foreground mb-6">
              Das Angebot wurde als vollwertiges CRM-Angebot gespeichert.
            </p>
            <p className="text-sm text-muted-foreground">
              Sie werden automatisch weitergeleitet...
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
